<template>
    <div class="payment__form">
        <div v-if="state.currentView == 'CAPITEC_INIT_VIEW' && state.useCustomerID === null">
            <div class="loader">
                <div class="loader-wrapper">
                    <div class="loader-icon"></div>
                    <p class="loader-text">{{ $t("ui.loader.text")}}</p>
                </div>
            </div>
        </div>
        <form v-else-if="state.currentView == 'CAPITEC_INIT_VIEW' && state.useCustomerID !== null" @submit.prevent="validateCapitecData">
            <p class="container__col-md-12 container__col-sm-12 payment__instruction">
                {{ state.useCustomerID ? $t("capitec.init.instructionOther") : $t("capitec.init.instruction") }}
            </p>
            <div v-if="!state.useCustomerID">
                <primary-select
                    class="container__col-md-12 container__col-sm-12"
                    :title="$t('capitec.input.capitecPaymentOption')"
                    :description-text="$t('ui.input.choose')"
                    @value-change="capitecPaymentOption = $event"
                    :options="state.capitecPaymentOptions"
                    :defaultValue="state.defaultCapitecOption"
                    :isRequired="getValueFromPath(state, 'inputs.capitecPaymentOption.required') || true"
                    inputName="capitecPaymentOption"
                />

                <primary-input
                    :title="$t('capitec.input.account')"
                    :number-only="true"
                    v-if="getValueFromPath(state, 'inputs.accountnumber') && getValueFromPath(capitecPaymentOption, 'name') === 'Account number'"
                    class="container__col-md-12 container__col-sm-12"
                    inputName="accountnumber"
                    type="tel"
                    prefix-image="bank-building"
                    placeholder="0123456789"
                    @value-change="accountNumber = $event"
                />

                <primary-input
                    :title="$t('capitec.input.phone')"
                    type="tel"
                    class="container__col-md-12 container__col-sm-12"
                    prefix-image="smart-phone"
                    placeholder="+27123456789"
                    v-if="getValueFromPath(state, 'inputs.phonenumber') && getValueFromPath(capitecPaymentOption, 'name') === 'Mobile number'"
                    :defaultValue="getValueFromPath(state, 'inputs.phonenumber.value') || null"
                    @value-change="telephone = $event"
                    inputName="telephone"
                    :number-only="true"
                />
            </div>
            <primary-input
                v-else
                :title="$t('capitec.input.customer')"
                :number-only="true"
                class="container__col-md-12 container__col-sm-12"
                inputName="customernumber"
                type="tel"
                prefix-image="user"
                placeholder="0123456789"
                @value-change="customerNumber = $event"
            />
            <PrimaryButton
                :content="
                    [$t('ui.pay.btn'), state.currency, state.amount.toLocaleString()]
                        .filter(Boolean)
                        .join(' ')
                "
                :disabled="!hasCompletedFields || state.processing"
                type="submit"
            />
        </form>

        <section v-else-if="state.currentView == 'CAPITEC_DETAILS_VIEW' && state.processing != true">
            <p class="container__col-md-12 container__col-sm-12 payment__instruction-2">
                <span class="f-14 item">
                    {{ $t("capitec.instruction.first") }}
                </span>
                <span class="f-14 item">
                    {{ $t("capitec.instruction.second") }}
                </span>
                <span class="f-14 item">
                    {{ $t("capitec.instruction.third") }}
                </span>
                <span role="timer" class="timer" v-html="timer">
                </span>
            </p>

            <div class="instructions">
                <ul class="instructions__list">
                    <li class="instructions__list-item">
                        <p>{{ $t("capitec.listitems.first") }}</p>
                        <span>
                            {{ $t("capitec.listitems.second") }}
                        </span>
                    </li>                    
                    <li class="instructions__list-item">
                        <p> {{ $t("capitec.listitems.third") }} </p>
                        <span>
                            {{ $t("capitec.listitems.fourth") }}
                        </span>
                    </li>                    
                    <li class="instructions__list-item">
                        <p> {{ $t("capitec.listitems.fifth") }} </p>
                        <span>
                            {{ $t("capitec.listitems.sixth") }} <b> {{ $t("capitec.listitems.seventh") }} </b> {{ $t("capitec.listitems.then") }} <b>
                                {{ $t("capitec.listitems.eight") }}
                            </b> {{ $t("capitec.listitems.ninth") }}
                        </span>
                    </li>
                </ul>
            </div>

            <!-- <PrimaryButton
                :content="$t('capitec.complete.btn')"
                type="button"
                @click="Next"
            /> -->
        </section>

        <template v-else-if="state.currentView == 'CAPITEC_CONFIRM_VIEW' && state.processing != true">
            <p class="container__col-md-12 container__col-sm-12 payment__instruction">
                {{ state.instruction }}
            </p>
            <div class="center">
                <div class="loader-icon"></div>
            </div>
            <p class="payment__instruction--refresh">{{$t("ui.transaction.refresh")}}.</p>
        </template> 
    </div>
</template>

<script>
import { ref, onBeforeUnmount, computed, watch } from "vue";
import { injectStore, mapState } from "redux-vuex";
import { getValueFromPath } from "@/support/helpers";
import PrimaryButton from "@/components/form/base-button";
import PrimaryInput from "@/components/form/base-input";
import PrimarySelect from "@/components/form/base-select";
import { isPlainObject } from "../../support/helpers";

export default {
    name: "Capitec",

    components: {
        PrimarySelect,
        PrimaryButton,
        PrimaryInput,
    },

    setup() {
        const store = injectStore();
        const checkoutData = ref(store.getState());
        const subscription = store.subscribe(() => {
            checkoutData.value = store.getState();
        });
        const capitecPaymentOption = ref("");
        const uiDeduction = 1;
        const accountNumber = ref("");
        const customerNumber = ref("");
        const telephone = ref("");
        const hasCompletedFields = computed(() => {
            if (checkoutData.value.view.context.useCustomerID) {
                return !!customerNumber.value;
            } 
            else if (capitecPaymentOption.value && capitecPaymentOption.value["name"] === "Account number") {
                return !!accountNumber.value;
            }
            else if (capitecPaymentOption.value && capitecPaymentOption.value["name"] === "Mobile number") {
                return !!telephone.value;
            }
            return false;
        });

        const timer = computed(() => {
            let time = "";
            if (checkoutData.value.view.context.timerCurrent) {
                let duration = checkoutData.value.view.context.timerCurrent.time;
                // 2 mins on the UI instead of the three minutes set on the runtime, hence the subtraction bit
                let minutes = Math.floor(duration / 60) - uiDeduction;
                let seconds = duration % 60;

                if (minutes > 0 && minutes != 1) {
                    time = `${minutes}mins and ${seconds}secs`;
                }
                else if (minutes == 1) {
                    time = `${minutes}min and ${seconds}secs`;
                } 
                else {
                    time = `${seconds}secs`;
                }
            }
            return time;
        });

        function validateCapitecData() {
            // eslint-disable-next-line no-useless-escape
            const specialChars = /[`!@#$%^&*()_+\\=\[\]{};':"\\|<>\\?~]/;

            const accNum = accountNumber.value ? String(accountNumber.value).replace(specialChars, "").replace(/\s/g, "").replace(/[()]/g, "") : "";
            const phone = telephone.value ? String(telephone.value).replace(specialChars, "").replace(/\s/g, "").replace(/[()]/g, "") : "";
            const customerID = customerNumber.value ? String(customerNumber.value).replace(specialChars, "").replace(/\s/g, "").replace(/[()]/g, "") : "";
            const data = {
                accountnumber: accNum,
                phonenumber: phone,
                customernumber: customerID,
            };

            submitCapitecData(data);
        }

        function submitCapitecData(data) {
            checkoutData.value.view.context.$actions.update(data);
            Next();
        }

        const Next = () => setTimeout(() => {
            checkoutData.value.view.context.$actions.next();
        }, 0);
        
        watch(capitecPaymentOption, (newVal, oldVal) => {
            if (oldVal && isPlainObject(oldVal) && newVal && isPlainObject(newVal)) {
                if (newVal["name"] !== oldVal["name"]) {
                    setTimeout(() => checkoutData.value.view.context.$actions.update({ capitecPaymentOption: newVal }), 0);
                }
            }
        });

        onBeforeUnmount(() => {
            subscription();
        });

        return {
            state: mapState({
                amount: (state) => getValueFromPath(state, "amount") || null,
                currency: (state) =>
                    getValueFromPath(state, "currency.current") || null,
                processing: (state) => getValueFromPath(state, "processing") || false,
                currentView: (state) => getValueFromPath(state, "view.current") || null,
                inputs: (state) => getValueFromPath(state, "view.context.inputs"),
                capitecPaymentOptions: (state) => getValueFromPath(state, "view.context.inputs.capitecPaymentOption.options"),
                defaultCapitecOption: (state) => getValueFromPath(state, "view.context.inputs.capitecPaymentOption.value"),
                useCustomerID: (state) => getValueFromPath(state, "view.context.useCustomerID") || false,
                instruction: (state) => getValueFromPath(state, "view.context.instruction") || null,
            }),
            getValueFromPath,
            capitecPaymentOption,
            accountNumber,
            customerNumber,
            telephone,
            timer,
            Next,
            hasCompletedFields,
            validateCapitecData,
        };
    },
};
</script>

<style lang="scss" scoped>
.payment__instruction {
    display: block;
    margin: 0 1rem 3.8rem 1rem;
    font-weight: normal;

    &-2 {
        margin: 0 0 2rem 0;
    }

    .item {
        font-style: normal;
        font-weight: 400;
        line-height: 1.8rem;
        color: #4F4F4F;
    }
}

.instructions {
    background: #FFFDFB;
    border: 1px solid rgba(139, 121, 121, 0.15);
    box-sizing: border-box;
    border-radius: .4rem;
    border-radius: 4px;
    padding: 3.2rem 2.4rem;
    margin-bottom: 2.4rem;

    &__list {
        list-style: none;
        padding-left: 0;
    }

    &__list-item {
        padding-left: 2rem;
        position: relative;

        &:not(:last-of-type) {
            margin-bottom: 2.4rem;
        }

        p {
            color: #222;
            font-size: 1.6rem;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
        }

        span {
            color: #828282;
            font-size: 1.4rem;
            font-style: normal;
            font-weight: 400;
            line-height: 1.6rem;

            b {
                color: #4F4F4F;
            }
        }

        &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 4px;
            width: 1em;
            height: 1em;
            background-repeat: no-repeat;
            background-image: url('../../assets/icons/list-item.svg');
        }
    }
}

.timer {
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 700;
    line-height: 1.8rem;
    color: $primary-color;
}

.f-14 {
    font-size: 1.4rem;
}

</style>